<template>
  <div class="resp-current-badge-holder-list">
    <ExBreadcrumbs
      :breadcrumbsList="menuItems"
      @onClickBreadcrumb="$emit('onClickBreadcrumb', $event)"
    />
    <div class="assignments-table-wrapper w-100 mt-5">
      <v-row class="ps-4 mb-4 top-section-class w-100">
        <v-col cols="12" class="search-checklist">
          <SearchBar
            :label="''"
            @onSearch="searchItems"
            type="button"
            ghostText="Search"
            :add="false"
            :counterSection="true"
            :filterBtn="false"
            :valueSearch="searchQuery"
            :levelCounter="{
              Apprentice: apprenticeCount,
              Professional: professionalCount,
              Coach: coachCount,
              Master: masterCount,
            }"
            :filterLevels="filterLevels"
            :removeCounterLevelFilter="removeCounterLevelFilter"
            @filterTypes="onFilterTypes"
          ></SearchBar>
        </v-col>
      </v-row>
    </div>
    <div v-if="filteredBadges.length > 0" class="display-all-badges">
      <ReadinessBadge
        v-for="(badge, index) in filteredBadges"
        :key="index + 1"
        :firstName="badge.firstName || badge.first_name || ''"
        :lastName="badge.lastName || badge.last_name || ''"
        :level="capitalizeFirstLetter(badge.badge_level)"
        :avatar="badge.img || badge.avatar || ''"
        :dot="'Good Standing'"
        :view="'ReadOnly'"
        :readinessView="true"
        :hideRemoveIcon="true"
        :hideIcons="true"
        :expiration_date="isEmployeeUser ? null : badge.expiration_date"
      />
    </div>
    <NoRecordFound
      v-if="filteredBadges.length == 0"
      :should-show-add-action="false"
      :title="'Employee'"
      :message="'Employee'"
      :btn-title="''"
      :subtitle="''"
      class="no-record-found"
      :showActiveCounter="false"
      :noBorder="true"
      :showClearAction="showClearAction"
      :clearLabel="clearBtnLabel"
      @onClear="clearSearchFilter()"
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed, watch } from "vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import ReadinessBadge from "@components/ReadinessBadge/ReadinessBadge.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";

const props = defineProps({
  selectedResponsibility: {
    type: Object,
    default: () => ({}),
  },
  selectedBadgeOption: {
    type: String,
    default: "",
  },
});

// Check if user has employee access (employee users should not see remove icon)
const accessPermission = computed(() => {
  try {
    return JSON.parse(localStorage.getItem("accessPermission")) || {};
  } catch {
    return {};
  }
});

const isEmployeeUser = computed(() => {
  return accessPermission.value?.has_employee === true;
});

const filterLevels = ref([]);
const searchQuery = ref("");
const removeCounterLevelFilter = ref(false);
watch(
  () => props.selectedBadgeOption,
  (newVal) => {
    if (newVal === "coach_badges") {
      filterLevels.value = ["Coach"];
    } else if (newVal === "master_badges") {
      filterLevels.value = ["Master"];
    } else {
      filterLevels.value = []; // fallback for other cases
    }
  },
  { immediate: true } // Run on initial mount too
);
const filteredBadges = computed(() => {
  const allBadges = props?.selectedResponsibility?.all_badges || [];

  // Normalize filter levels
  const normalizedFilters = filterLevels.value.map((level) =>
    level.toLowerCase()
  );

  // Apply level filter
  let result = normalizedFilters.length
    ? allBadges.filter((badge) =>
        normalizedFilters.includes(badge.badge_level.toLowerCase())
      )
    : allBadges;

  // Apply search filter
  if (searchQuery.value.trim() !== "") {
    const searchTerms = searchQuery.value
      .split(/\s+/)
      .filter((term) => term.length > 0);
    result = result.filter((badge) => {
      // Handle both fullName and full_name field variations
      const fullName = badge.fullName || badge.full_name || "";
      const firstName = badge.firstName || badge.first_name || "";
      const lastName = badge.lastName || badge.last_name || "";

      // Combine all searchable fields
      const searchableText =
        `${fullName} ${firstName} ${lastName}`.toLowerCase();

      return searchTerms.every((term) =>
        searchableText.includes(term.toLowerCase())
      );
    });
  }

  // Define badge level order
  const levelOrder = {
    master: 1,
    coach: 2,
    professional: 3,
    apprentice: 4,
  };

  // Sort based on levelOrder
  result.sort((a, b) => {
    return (
      levelOrder[a.badge_level.toLowerCase()] -
      levelOrder[b.badge_level.toLowerCase()]
    );
  });

  return result;
});

const clearBtnLabel = computed(() => {
  if (searchQuery.value && filterLevels.value.length > 0) {
    return "Clear All";
  } else if (filterLevels.value.length > 0) {
    return "Clear Filter";
  } else if (searchQuery.value) {
    return "Clear Search";
  } else {
    return "";
  }
});
const showClearAction = computed(() => {
  return searchQuery.value || filterLevels.value.length > 0;
});

const apprenticeCount = computed(() => {
  return filteredBadges?.value?.filter(
    (item) => item.badge_level === "apprentice"
  ).length;
});
const coachCount = computed(() => {
  return filteredBadges?.value?.filter((item) => item.badge_level === "coach")
    .length;
});
const masterCount = computed(() => {
  return filteredBadges?.value?.filter((item) => item.badge_level === "master")
    .length;
});
const professionalCount = computed(() => {
  return filteredBadges?.value?.filter(
    (item) => item.badge_level === "professional"
  ).length;
});

const menuItems = ref([
  { text: "Responsibility Directory", disabled: false },
  {
    text: `${props?.selectedResponsibility.name}`,
    disabled: false,
  },
  { text: "Current Badge Holders", disabled: true, href: "" },
]);
const onFilterTypes = (data) => {
  if (removeCounterLevelFilter.value) {
    removeCounterLevelFilter.value = false;
  }
  filterLevels.value = data;
};
const searchItems = (searchData) => {
  searchQuery.value = searchData;
};
const clearSearchFilter = () => {
  if (filterLevels.value.length > 0) {
    removeCounterLevelFilter.value = true;
  }
  searchQuery.value = "";
  filterLevels.value = [];
};

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};
</script>

<style scoped>
.resp-current-badge-holder-list {
  padding: 16px 18px;
  border: 1px solid #d0d5dd !important;
  border-radius: 12px !important;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.display-all-badges {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
</style>
